import React from 'react';
import { CHIPOTLE_CONTENT_LOOK } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content-look';
import Hero from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content/hero';
import { formatCustomerGroup } from '@loomstateorg/shared-frontend/helpers/formatter';
import { useFormat } from 'helpers/hooks/useFormat';
import { ALL_USERS_TAG } from 'helpers/hooks/usePermissionsChecker';

interface Props {
  data: any;
  customerGroup: any;
}

/** Extract URL from Sanity / Frontastic file or image asset shapes */
function getMediaFileUrl(media: unknown): string | undefined {
  if (!media) return undefined;
  if (typeof media === 'string') return media;

  const asset = media as Record<string, unknown>;
  if (typeof asset.url === 'string' && asset.url) return asset.url;

  const nestedAsset = asset.asset as Record<string, unknown> | undefined;
  if (nestedAsset && typeof nestedAsset.url === 'string' && nestedAsset.url) return nestedAsset.url;

  const fields = asset.fields as Record<string, unknown> | undefined;
  const file = fields?.file as Record<string, unknown> | undefined;
  if (file && typeof file.url === 'string' && file.url) return file.url;

  return undefined;
}

function mimeTypeFromUrl(url: string): string {
  if (/\.webm(\?|#|$)/i.test(url)) return 'video/webm';
  if (/\.mp4(\?|#|$)/i.test(url)) return 'video/mp4';
  return 'video/mp4';
}

/** Extract MIME type from Sanity / Frontastic file asset shapes */
function getMediaMimeType(media: unknown, url?: string): string | undefined {
  if (media && typeof media === 'object') {
    const asset = media as Record<string, unknown>;
    const nestedAsset = asset.asset as Record<string, unknown> | undefined;
    if (typeof nestedAsset?.mimeType === 'string' && nestedAsset.mimeType) {
      return nestedAsset.mimeType;
    }

    const fields = asset.fields as Record<string, unknown> | undefined;
    const file = fields?.file as Record<string, unknown> | undefined;
    if (typeof file?.mimeType === 'string' && file.mimeType) {
      return file.mimeType;
    }
  }

  return url ? mimeTypeFromUrl(url) : undefined;
}

const ComposedHeroTastic = ({ data, customerGroup }: Props) => {
  const { formatMessage } = useFormat({ name: 'common' });

  if (!data) {
    return <div>Loading...</div>;
  }

  const heros = data?.data?.dataSource?.items?.[0]?.fields?.components;

  const formattedCustomerGroup = formatCustomerGroup(customerGroup?.obj?.key!);

  const filteredData = heros?.filter((item: { metadata: { tags: any[] } }) =>
    item?.metadata?.tags?.some((tag) => tag.sys.id === formattedCustomerGroup || tag.sys.id === ALL_USERS_TAG),
  );

  const isSingleHero = filteredData?.length === 1;

  return (
    <>
      <section
        data-testid="hero"
        aria-label={formatMessage({ id: 'hero', defaultMessage: 'Hero' })}
        className={`grid ${isSingleHero ? 'grid-cols-1' : 'grid-cols-1 gap-4 lg:grid-cols-2'}`}
      >
        {filteredData?.map((hero: any, index: number) => {
          const tile = hero?.fields ?? hero;
          const videoMedia = tile?.video ?? tile?.videoDesktop;
          const videoUrl = getMediaFileUrl(videoMedia);
          const videoMimeType = getMediaMimeType(videoMedia, videoUrl);
          const imageUrl = videoUrl ? undefined : getMediaFileUrl(tile?.image);

          return (
            <div key={index} className="col-span-1">
              <Hero
                look={CHIPOTLE_CONTENT_LOOK}
                image={imageUrl}
                videoUrl={videoUrl}
                videoMimeType={videoMimeType}
                title={tile?.title}
                titleColor={tile?.titleColor}
                titleFontSize={tile?.titleFontSize}
                subtitle={tile?.subtitle}
                subtitleColor={tile?.subtitleColor}
                subtitleFontSize={tile?.subtitleFontSize}
                ctaLabel={tile?.ctaLabel}
                ctaReference={tile?.ctaReference}
                buttonBackgroundColor={tile?.buttonBackgroundColor}
                buttonBackgroundColorHover={tile?.buttonBackgroundColorHover}
                buttonBackgroundTransparent={tile?.buttonBackgroundTransparent}
                buttonBorderRadius={tile?.buttonBorderRadius}
                buttonHorizontalAlign={tile?.buttonHorizontalAlign}
                buttonVerticalAlign={tile?.buttonVerticalAlign}
                buttonTextColor={tile?.buttonTextColor}
                buttonFontSize={tile?.buttonFontSize}
                contentDirection={tile?.contentDirection}
              />
            </div>
          );
        })}
      </section>
    </>
  );
};

export default ComposedHeroTastic;
